import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Network, Search, Star, UserPlus, CheckCircle, XCircle, Clock,
  MapPin, Filter, MessageCircle, Phone, Mail, UserMinus, ShieldBan,
  Flag, ChevronDown, Stethoscope, Building2, X,
} from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Dialog from '@radix-ui/react-dialog';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { getInitials } from '@/lib/utils';
import { useAuthStore, ROL } from '@/store/auth.store';
import { ReferirPacienteModal } from '@/components/red/ReferirPacienteModal';
import { MedicoDirectorio } from '@/components/red/MedicoPerfilDrawer';
import { useChatStore } from '@/store/chat.store';
import { getErrorMessage } from '@/lib/errors';

type Tab = 'directorio' | 'contactos';

// ─── Tarjeta de médico ───────────────────────────────────────────────────────

function MedicoCard({
  medico,
  esContacto,
  solicitudPendiente,
  onToggleFavorito,
  onAgregarRed,
  onEliminarContacto,
  onReferir,
  onBloquear,
  onReportar,
  onChat,
}: {
  medico: MedicoDirectorio;
  esContacto: boolean;
  solicitudPendiente: boolean;
  onToggleFavorito: (id: string) => void;
  onAgregarRed: (id: string) => void;
  onEliminarContacto: (id: string) => void;
  onReferir: (m: MedicoDirectorio) => void;
  onBloquear: (id: string, nombre: string) => void;
  onReportar: (id: string, nombre: string) => void;
  onChat: (id: string) => void;
}) {
  const foto = medico.usuario?.fotoUrl;
  const telMedico = medico.usuario?.telefono;
  const emailMedico = medico.usuario?.email;
  const telClinica = medico.clinica?.telefono;
  const tel = telMedico || telClinica || null;
  const email = emailMedico || null;
  const ubicacion = [medico.clinica?.ciudad, medico.clinica?.estado].filter(Boolean).join(', ');

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden flex flex-col hover:shadow-md hover:border-primary-100 transition-all group">
      {/* Cabecera con gradiente + avatar */}
      <div className="relative h-20 bg-gradient-to-br from-primary-500 to-primary-700 flex-shrink-0">
        {/* Favorito */}
        <button
          onClick={() => onToggleFavorito(medico.id)}
          className="absolute top-2 right-2 p-1.5 rounded-lg bg-white/20 hover:bg-white/35 transition-colors"
        >
          <Star
            size={14}
            className={medico.esFavorito ? 'text-amber-300 fill-amber-300' : 'text-white/70'}
          />
        </button>
        {/* Avatar centrado */}
        <div className="absolute -bottom-8 left-1/2 -translate-x-1/2">
          <div className="w-16 h-16 rounded-full border-4 border-white shadow-md overflow-hidden bg-primary-100 flex items-center justify-center text-primary-600 font-bold text-lg">
            {foto
              ? <img src={foto} alt={medico.nombre} className="w-full h-full object-cover" />
              : getInitials(medico.nombre)
            }
          </div>
        </div>
      </div>

      {/* Cuerpo */}
      <div className="pt-10 px-4 pb-4 flex flex-col flex-1 gap-3">
        {/* Nombre + especialidad */}
        <div className="text-center">
          <p className="font-semibold text-sm text-text-primary leading-tight">Dr. {medico.nombre}</p>
          {medico.especialidad && (
            <span className="inline-block mt-1.5 px-3 py-0.5 bg-primary-50 text-primary-600 text-[11px] font-semibold rounded-full">
              {medico.especialidad.nombre}
            </span>
          )}
        </div>

        {/* Clínica + dirección */}
        {medico.clinica && (
          <div className="space-y-0.5">
            {medico.clinica.nombre && (
              <div className="flex items-center gap-1.5 justify-center">
                <Building2 size={11} className="text-text-secondary flex-shrink-0" />
                <p className="text-xs text-text-secondary truncate">{medico.clinica.nombre}</p>
              </div>
            )}
            {ubicacion && (
              <div className="flex items-center gap-1.5 justify-center">
                <MapPin size={11} className="text-text-secondary flex-shrink-0" />
                <p className="text-xs text-text-secondary truncate">{ubicacion}</p>
              </div>
            )}
          </div>
        )}

        {/* Subespecialidades */}
        {medico.subespecialidades && (
          <div className="flex flex-wrap gap-1 justify-center">
            {medico.subespecialidades.split(',').slice(0, 2).map(s => s.trim()).filter(Boolean).map(tag => (
              <span key={tag} className="px-2 py-0.5 bg-gray-100 text-text-secondary rounded-full text-[10px]">{tag}</span>
            ))}
          </div>
        )}

        {/* Botones de contacto rápido (si es contacto) */}
        {esContacto && (tel || email) && (
          <div className="flex items-center justify-center gap-2">
            {tel && (
              <a
                href={`tel:${tel}`}
                onClick={e => e.stopPropagation()}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-50 text-green-700 text-xs font-medium hover:bg-green-100 transition-colors"
              >
                <Phone size={12} /> Llamar
              </a>
            )}
            {email && (
              <a
                href={`mailto:${email}`}
                onClick={e => e.stopPropagation()}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 text-xs font-medium hover:bg-blue-100 transition-colors"
              >
                <Mail size={12} /> Email
              </a>
            )}
            <button
              onClick={() => onChat(medico.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-50 text-primary-600 text-xs font-medium hover:bg-primary-100 transition-colors"
            >
              <MessageCircle size={12} /> Chat
            </button>
          </div>
        )}

        {/* Separador */}
        <div className="h-px bg-gray-100 -mx-4" />

        {/* Acciones principales */}
        <div className="flex items-center gap-2">
          {/* Referir paciente */}
          <button
            onClick={() => onReferir(medico)}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-violet-50 text-violet-700 text-xs font-semibold hover:bg-violet-100 transition-colors"
          >
            <Network size={13} /> Referir
          </button>

          {/* Agregar / Eliminar / Pendiente */}
          {esContacto ? (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-gray-100 text-text-secondary text-xs font-semibold hover:bg-gray-200 transition-colors">
                  <ChevronDown size={13} /> En mi red
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="end"
                  sideOffset={4}
                  className="z-50 min-w-[180px] bg-white rounded-xl shadow-lg border border-gray-100 py-1.5"
                >
                  <DropdownMenu.Item
                    onSelect={() => onEliminarContacto(medico.id)}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-text-primary hover:bg-gray-50 cursor-pointer outline-none"
                  >
                    <UserMinus size={14} className="text-gray-400" /> Eliminar de mi red
                  </DropdownMenu.Item>
                  <DropdownMenu.Separator className="my-1 h-px bg-gray-100" />
                  <DropdownMenu.Item
                    onSelect={() => onBloquear(medico.id, medico.nombre)}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 cursor-pointer outline-none"
                  >
                    <ShieldBan size={14} /> Bloquear
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    onSelect={() => onReportar(medico.id, medico.nombre)}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 cursor-pointer outline-none"
                  >
                    <Flag size={14} /> Reportar
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          ) : solicitudPendiente ? (
            <button
              disabled
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-amber-50 text-amber-600 text-xs font-semibold cursor-default"
            >
              <Clock size={13} /> Pendiente
            </button>
          ) : (
            <button
              onClick={() => onAgregarRed(medico.id)}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-primary-50 text-primary-600 text-xs font-semibold hover:bg-primary-100 transition-colors"
            >
              <UserPlus size={13} /> Agregar a mi red
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Modal de reporte ────────────────────────────────────────────────────────

function ReportarModal({
  medicoId,
  medicoNombre,
  onClose,
  onConfirm,
  loading,
}: {
  medicoId: string;
  medicoNombre: string;
  onClose: () => void;
  onConfirm: (motivo: string) => void;
  loading: boolean;
}) {
  const [motivo, setMotivo] = useState('');
  return (
    <Dialog.Root open onOpenChange={open => { if (!open) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-50" />
        <Dialog.Content className="fixed inset-x-0 bottom-0 md:inset-x-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:bottom-auto z-50 w-full md:max-w-md bg-white rounded-t-3xl md:rounded-2xl shadow-xl p-6 focus:outline-none max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-red-600">
              <Flag size={18} />
              <Dialog.Title className="text-base font-semibold">Reportar a Dr. {medicoNombre}</Dialog.Title>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary">
              <X size={16} />
            </button>
          </div>
          <p className="text-sm text-text-secondary mb-4">
            El médico será bloqueado automáticamente y se enviará una alerta al equipo de administración.
          </p>
          <label className="block text-xs font-semibold text-text-primary mb-1.5">Motivo del reporte</label>
          <textarea
            value={motivo}
            onChange={e => setMotivo(e.target.value)}
            rows={4}
            placeholder="Describe el motivo del reporte..."
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 resize-none"
          />
          <div className="flex gap-2 mt-4">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-text-secondary hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={() => onConfirm(motivo)}
              disabled={!motivo.trim() || loading}
              className="flex-1 py-2.5 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-40 transition-colors"
            >
              {loading ? 'Enviando...' : 'Enviar reporte'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Página principal ────────────────────────────────────────────────────────

export function RedMedicaPage() {
  const qc = useQueryClient();
  const { usuario } = useAuthStore();
  const canManage = usuario?.rolId === ROL.MEDICO || usuario?.rolId === ROL.MEDICO_ADMIN || usuario?.rolId === ROL.SUPER_ADMIN;
  const { openChat } = useChatStore();
  const [searchParams] = useSearchParams();

  const [tab, setTab] = useState<Tab>(() => {
    const t = searchParams.get('tab');
    return t === 'contactos' ? 'contactos' : 'directorio';
  });

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t === 'contactos') setTab('contactos');
    else if (t === 'directorio') setTab('directorio');
  }, [searchParams]);
  const [referirAbierto, setReferirAbierto] = useState(false);
  const [medicoParaReferir, setMedicoParaReferir] = useState<MedicoDirectorio | null>(null);
  const [reportarTarget, setReportarTarget] = useState<{ id: string; nombre: string } | null>(null);

  // Filtros directorio
  const [q, setQ] = useState('');
  const [especialidadFiltro, setEspecialidadFiltro] = useState('');

  // ── Queries ──────────────────────────────────────────────────────────────

  const { data: directorio = [], isLoading: loadingDir } = useQuery<MedicoDirectorio[]>({
    queryKey: ['red-directorio', q, especialidadFiltro],
    queryFn: () => {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (especialidadFiltro) params.set('especialidadId', especialidadFiltro);
      return api.get(`/api/red/directorio?${params}`).then(r => r.data);
    },
  });

  const { data: favoritos = [] } = useQuery({
    queryKey: ['red-favoritos'],
    queryFn: () => api.get('/api/red/favoritos').then(r => r.data),
    enabled: canManage,
  });

  const { data: contactos = [], isLoading: loadingContactos } = useQuery({
    queryKey: ['red-contactos'],
    queryFn: () => api.get('/api/red/contactos').then(r => r.data),
  });

  const { data: solicitudes = [] } = useQuery({
    queryKey: ['red-solicitudes'],
    queryFn: () => api.get('/api/red/solicitudes').then(r => r.data),
  });

  const { data: especialidades = [] } = useQuery({
    queryKey: ['especialidades'],
    queryFn: () => api.get('/api/catalogos/especialidades').then(r => r.data),
  });

  const { data: profile } = useQuery({
    queryKey: ['auth-me'],
    queryFn: () => api.get('/api/auth/me').then(r => r.data),
    staleTime: 5 * 60_000,
  });
  const miMedicoId: string | undefined = profile?.medico?.id;

  // ── Sets computados ──────────────────────────────────────────────────────

  const favoritosIds = useMemo(
    () => new Set(favoritos.map((f: any) => f.medicoFavoritoId)),
    [favoritos],
  );

  const contactosIds = useMemo(() => {
    const s = new Set<string>();
    contactos.forEach((c: any) => {
      if (c.estado === 'aceptado') {
        const otroId = c.medicoSolicitanteId === miMedicoId ? c.medicoReceptorId : c.medicoSolicitanteId;
        if (otroId) s.add(otroId);
      }
    });
    return s;
  }, [contactos, miMedicoId]);

  const solicitudesEnviadasIds = useMemo(() => {
    const s = new Set<string>();
    solicitudes.forEach((sol: any) => {
      if (sol.medicoSolicitanteId === miMedicoId) s.add(sol.medicoReceptorId);
    });
    return s;
  }, [solicitudes, miMedicoId]);

  // ── Mutaciones ───────────────────────────────────────────────────────────

  const favoritoMutation = useMutation({
    mutationFn: (id: string) => api.post(`/api/red/favoritos/${id}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['red-favoritos'] }),
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al actualizar favorito')),
  });

  const agregarRedMutation = useMutation({
    mutationFn: (medicoReceptorId: string) => api.post('/api/red/contactos', { medicoReceptorId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['red-solicitudes'] });
      toast.success('Solicitud enviada');
    },
    onError: (e: any) => toast.error(getErrorMessage(e, 'Error al enviar solicitud')),
  });

  const aceptarMutation = useMutation({
    mutationFn: (id: string) => api.patch(`/api/red/contactos/${id}/aceptar`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['red-contactos'] });
      qc.invalidateQueries({ queryKey: ['red-solicitudes'] });
      toast.success('Solicitud aceptada');
    },
  });

  const rechazarMutation = useMutation({
    mutationFn: (id: string) => api.patch(`/api/red/contactos/${id}/rechazar`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['red-contactos'] });
      qc.invalidateQueries({ queryKey: ['red-solicitudes'] });
      toast.success('Solicitud rechazada');
    },
  });

  const eliminarContactoMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/red/contactos/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['red-contactos'] });
      toast.success('Contacto eliminado de tu red');
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al eliminar contacto')),
  });

  const bloquearMutation = useMutation({
    mutationFn: (medicoId: string) => api.post(`/api/red/bloqueos/${medicoId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['red-contactos'] });
      qc.invalidateQueries({ queryKey: ['red-directorio'] });
      toast.success('Médico bloqueado');
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al bloquear')),
  });

  const reportarMutation = useMutation({
    mutationFn: ({ medicoId, motivo }: { medicoId: string; motivo: string }) =>
      api.post(`/api/red/reportes/${medicoId}`, { motivo }),
    onSuccess: () => {
      setReportarTarget(null);
      qc.invalidateQueries({ queryKey: ['red-contactos'] });
      qc.invalidateQueries({ queryKey: ['red-directorio'] });
      toast.success('Reporte enviado. El médico ha sido bloqueado.');
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al enviar el reporte')),
  });

  // ── Helpers ──────────────────────────────────────────────────────────────

  function handleReferir(medico: MedicoDirectorio) {
    setMedicoParaReferir(medico);
    setReferirAbierto(true);
  }

  function getContactoId(medicoId: string): string | undefined {
    return (contactos as any[]).find(c =>
      (c.medicoSolicitanteId === miMedicoId && c.medicoReceptorId === medicoId) ||
      (c.medicoSolicitanteId === medicoId && c.medicoReceptorId === miMedicoId),
    )?.id;
  }

  function getMedicoDeContacto(c: any): MedicoDirectorio {
    const otro = c.medicoSolicitanteId === miMedicoId ? c.medicoReceptor : c.medicoSolicitante;
    return { ...otro, esFavorito: favoritosIds.has(otro?.id) };
  }

  const directorioEnriquecido: MedicoDirectorio[] = directorio.map(m => ({
    ...m,
    esFavorito: favoritosIds.has(m.id),
    esContacto: contactosIds.has(m.id),
    solicitudPendiente: solicitudesEnviadasIds.has(m.id),
  }));

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="space-y-5">
      {/* Header + Tabs */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-text-primary">Red Médica</h2>
          <p className="text-sm text-text-secondary mt-0.5">
            {tab === 'directorio'
              ? `${directorioEnriquecido.length} médico${directorioEnriquecido.length !== 1 ? 's' : ''}`
              : `${contactos.length} contacto${contactos.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        {canManage && tab === 'contactos' && (
          <button
            onClick={() => setReferirAbierto(true)}
            className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
          >
            <Network size={15} /> Referir paciente
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        {([['directorio', 'Directorio'], ['contactos', 'Mis contactos']] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
              tab === key ? 'bg-white text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            {label}
            {key === 'contactos' && solicitudes.length > 0 && (
              <span className="ml-2 px-1.5 py-0.5 bg-amber-400 text-white text-xs rounded-full">
                {solicitudes.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── TAB DIRECTORIO ─────────────────────────────────────────────────── */}
      {tab === 'directorio' && (
        <div className="space-y-4">
          {/* Buscador */}
          <div className="relative">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nombre, especialidad..."
              className="w-full h-11 pl-10 pr-4 rounded-2xl bg-gray-50 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-200 border-none transition-all"
              value={q}
              onChange={e => setQ(e.target.value)}
            />
          </div>

          {/* Filtro por especialidad — pills */}
          {especialidades.length > 0 && (
            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
              <div className="flex items-center gap-1.5 flex-shrink-0 text-text-secondary">
                <Filter size={13} />
                <span className="text-xs font-medium">Especialidad:</span>
              </div>
              <button
                onClick={() => setEspecialidadFiltro('')}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  !especialidadFiltro
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-text-secondary hover:bg-gray-200'
                }`}
              >
                Todas
              </button>
              {(especialidades as any[]).map((esp: any) => (
                <button
                  key={esp.id}
                  onClick={() => setEspecialidadFiltro(e => e === esp.id ? '' : esp.id)}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    especialidadFiltro === esp.id
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-text-secondary hover:bg-gray-200'
                  }`}
                >
                  {esp.nombre}
                </button>
              ))}
            </div>
          )}

          {/* Grid */}
          {loadingDir ? (
            <div className="text-center py-16 text-text-secondary text-sm">Buscando médicos...</div>
          ) : directorioEnriquecido.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
              <Stethoscope size={48} className="mx-auto text-gray-200 mb-4" />
              <p className="font-medium text-text-primary">Sin resultados</p>
              <p className="text-sm text-text-secondary mt-1">Prueba con otros filtros</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
              {directorioEnriquecido.map(medico => (
                <MedicoCard
                  key={medico.id}
                  medico={medico}
                  esContacto={medico.esContacto ?? false}
                  solicitudPendiente={medico.solicitudPendiente ?? false}
                  onToggleFavorito={id => favoritoMutation.mutate(id)}
                  onAgregarRed={id => agregarRedMutation.mutate(id)}
                  onEliminarContacto={id => {
                    const cId = getContactoId(id);
                    if (cId) eliminarContactoMutation.mutate(cId);
                  }}
                  onReferir={handleReferir}
                  onBloquear={(id, nombre) => {
                    if (confirm(`¿Bloquear a Dr. ${nombre}? Esta acción es recíproca.`)) {
                      bloquearMutation.mutate(id);
                    }
                  }}
                  onReportar={(id, nombre) => setReportarTarget({ id, nombre })}
                  onChat={openChat}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── TAB MIS CONTACTOS ──────────────────────────────────────────────── */}
      {tab === 'contactos' && (
        <div className="space-y-6">
          {/* Solicitudes pendientes */}
          {solicitudes.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Clock size={16} className="text-amber-500" />
                <h3 className="text-sm font-semibold text-text-primary">Solicitudes pendientes</h3>
              </div>
              <div className="bg-white rounded-xl border border-amber-200 divide-y divide-amber-50">
                {(solicitudes as any[]).map((s: any) => {
                  const medico = s.medicoSolicitante || s.medicoReceptor;
                  return (
                    <div key={s.id} className="flex items-center justify-between px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 text-sm font-bold">
                          {getInitials(medico?.nombre || 'M')}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-text-primary">{medico?.nombre}</p>
                          <p className="text-xs text-text-secondary">{medico?.especialidad?.nombre || 'Médico'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => aceptarMutation.mutate(s.id)}
                          disabled={aceptarMutation.isPending}
                          className="flex items-center gap-1 px-2.5 py-1 bg-green-50 text-green-700 rounded-lg text-xs font-medium hover:bg-green-100 disabled:opacity-50"
                        >
                          <CheckCircle size={12} /> Aceptar
                        </button>
                        <button
                          onClick={() => rechazarMutation.mutate(s.id)}
                          disabled={rechazarMutation.isPending}
                          className="flex items-center gap-1 px-2.5 py-1 bg-red-50 text-red-600 rounded-lg text-xs font-medium hover:bg-red-100 disabled:opacity-50"
                        >
                          <XCircle size={12} /> Rechazar
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Lista de contactos */}
          {loadingContactos ? (
            <div className="text-center py-16 text-text-secondary text-sm">Cargando...</div>
          ) : contactos.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
              <Network size={48} className="mx-auto text-gray-200 mb-4" />
              <p className="font-medium text-text-primary">Sin contactos en tu red</p>
              <p className="text-sm text-text-secondary mt-1">Explora el directorio y agrega médicos</p>
              <button
                onClick={() => setTab('directorio')}
                className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700"
              >
                Ir al directorio
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
              {(contactos as any[]).map(c => {
                const medico = getMedicoDeContacto(c);
                if (!medico?.id) return null;
                return (
                  <MedicoCard
                    key={c.id}
                    medico={medico}
                    esContacto={true}
                    solicitudPendiente={false}
                    onToggleFavorito={id => favoritoMutation.mutate(id)}
                    onAgregarRed={() => {}}
                    onEliminarContacto={() => eliminarContactoMutation.mutate(c.id)}
                    onReferir={handleReferir}
                    onBloquear={(id, nombre) => {
                      if (confirm(`¿Bloquear a Dr. ${nombre}? Se eliminará de tus contactos y bloqueará toda interacción.`)) {
                        bloquearMutation.mutate(id);
                      }
                    }}
                    onReportar={(id, nombre) => setReportarTarget({ id, nombre })}
                    onChat={openChat}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Modal referir */}
      {referirAbierto && (
        <ReferirPacienteModal
          medicoPreseleccionado={medicoParaReferir ?? undefined}
          onClose={() => { setReferirAbierto(false); setMedicoParaReferir(null); }}
        />
      )}

      {/* Modal reportar */}
      {reportarTarget && (
        <ReportarModal
          medicoId={reportarTarget.id}
          medicoNombre={reportarTarget.nombre}
          onClose={() => setReportarTarget(null)}
          onConfirm={motivo => reportarMutation.mutate({ medicoId: reportarTarget.id, motivo })}
          loading={reportarMutation.isPending}
        />
      )}
    </div>
  );
}
